import React, { useState, useEffect, useCallback } from "react";
import { Formik } from "formik";
import * as Yup from "yup";

import {
  Paper,
  Typography,
  Tabs,
  Tab,
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Badge,
  Alert,
  CircularProgress,
} from "@mui/material";

import {
  UploadFile,
  Description,
  Visibility,
  Delete,
  Done,
  Archive,
} from "@mui/icons-material";

import {
  deleteContract,
  getContracts,
  getUnreadFileCounts,
  markFileAsRead,
  moveClientFileToClientArchive,
  uploadContact,
} from "../services/source";
import { openFileUrl } from "../services/fileUtils";
import auth from "../services/auth";

/* =========================
    VALIDATION
  ========================= */
const getContractSchema = (tabIndex) => {
  if (tabIndex === 3) {
    // Copia recibos - only inicio required
    return Yup.object({
      inicio: Yup.string().required("Data e obrigatoria"),
      fim: Yup.string(),
    });
  }
  if ([4, 5, 6].includes(tabIndex)) {
    // Relatorio tabs - no dates required
    return Yup.object({
      inicio: Yup.string(),
      fim: Yup.string(),
    });
  }
  // All other tabs - both required with date range validation
  return Yup.object({
    inicio: Yup.string().required("Data de inicio e obrigatoria"),
    fim: Yup.string().required("Data de fim e obrigatoria"),
  }).test(
    "dateRange",
    "Data de inicio nao pode ser maior que data de fim",
    function (values) {
      const { inicio, fim } = values;
      if (!inicio || !fim) return true;
      return new Date(inicio) <= new Date(fim);
    },
  );
};

/* =========================
    CONTRACT CATEGORIES
  ========================= */
const contractCategories = [
  "Copia do contrato assinado",
  "Copia de adendas",
  "Copia das faturas",
  "Copia recibos",
  "Relatorio do que falta facturar",
  "Relatorio do que falta pagar",
  "Relatorio do trabalho em falta",
];

const Contract = ({ clientId, companyId: companyIdProp }) => {
  const currentUser = auth.getCurrentUser();
  const userId = currentUser?.data?.id;
  const userType = currentUser?.data?.type;
  const companyId =
    companyIdProp ||
    currentUser?.data?.companyId ||
    currentUser?.data?.id ||
    "";

  const [selectedContractTab, setSelectedContractTab] = useState(0);
  const [openingFileId] = useState(0);
  const [contracts, setContracts] = useState({});
  const [dbContracts, setDbContracts] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [unreadByCategory, setUnreadByCategory] = useState({});
  const [movingFileId, setMovingFileId] = useState(0);
  const [archiveError, setArchiveError] = useState("");
  const [archiveSuccess, setArchiveSuccess] = useState("");
  const FILE_BASE_URL = process.env.REACT_APP_API_URL;

  const refreshUnreadCount = useCallback(async () => {
    if (!clientId || !userId || !userType) {
      setUnreadByCategory({});
      return;
    }

    const res = await getUnreadFileCounts({
      user_id: userId,
      user_type: userType,
      client_id: clientId,
    });

    if (res?.success) {
      setUnreadByCategory(res?.data?.contratos_por_categoria || {});
    }
  }, [clientId, userId, userType]);

  const loadContracts = useCallback(async () => {
    if (!clientId) return;

    const res = await getContracts(clientId);

    if (res?.success) {
      setDbContracts(res.data);
    }
  }, [clientId]);

  useEffect(() => {
    if (!clientId) return;

    const load = async () => {
      const res = await getContracts(clientId);
      if (res?.success) setDbContracts(res.data);
    };

    load();
  }, [clientId]);

  useEffect(() => {
    loadContracts();
  }, [loadContracts]);

  useEffect(() => {
    refreshUnreadCount();
  }, [refreshUnreadCount]);

  /* =========================
     LOCAL UPLOAD
  ========================= */
  const handleContractUpload = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    const file = files[0];

    const fileEntry = {
      file,
      name: file.name,
      size: file.size,
      inicio: "",
      fim: "",
      uploaded: false,
    };

    setContracts((prev) => ({
      ...prev,
      [selectedContractTab]: [...(prev[selectedContractTab] || []), fileEntry],
    }));
    event.target.value = "";
  };

  /* =========================
     UPLOAD
  ========================= */
  const uploadFileToServer = async (fileEntry, category) => {
    const formData = new FormData();

    formData.append("file", fileEntry.file);
    formData.append("cliente_id", clientId);
    formData.append("company_id", companyId);
    formData.append("user_id", userId || "");
    formData.append("user_type", userType || "");
    formData.append("categoria", category);
    formData.append("data_inicio", fileEntry.inicio || "");
    formData.append("data_fim", fileEntry.fim || "");
    formData.append("observacoes", "");

    const res = await uploadContact(formData);
    return res?.data?.success ? Number(res?.data?.id || 0) : 0;
  };

  /* =========================
     REMOVE LOCAL ONLY
  ========================= */
  const removeContract = (index) => {
    setContracts((prev) => ({
      ...prev,
      [selectedContractTab]: (prev[selectedContractTab] || []).filter(
        (_, i) => i !== index,
      ),
    }));
  };

  /* =========================
     FILTER DB BY TAB
  ========================= */
  const filteredDbContracts = dbContracts.filter(
    (con) => con.categoria === contractCategories[selectedContractTab],
  );

  const localContract = contracts[selectedContractTab] || [];

  /* =========================
    DELETE DOCUMENT
  ========================= */
  const handleDeleteContract = (file) => {
    setSelectedFile(file);
    setDeleteModalOpen(true);
  };

  const confirmDeleteContract = async () => {
    if (!selectedFile) return;

    const res = await deleteContract(selectedFile.id);
    const data = res?.data;

    if (data?.success) {
      await loadContracts();
    } else {
      console.error(data);
      alert("Erro ao apagar documento");
    }

    setDeleteModalOpen(false);
    setSelectedFile(null);
  };

  const handleOpenContract = async (file) => {
    try {
      const ok = await openFileUrl(
        file.caminho,
        file.nome_original || file.nome_ficheiro,
      );
      if (!ok) {
        window.open(`${FILE_BASE_URL}/${file.caminho}`, "_blank");
      }
    } catch (err) {
      window.open(`${FILE_BASE_URL}/${file.caminho}`, "_blank");
    }

    if (!userId || !userType || !file?.id) return;

    await markFileAsRead({
      user_id: userId,
      user_type: userType,
      file_type: "contract",
      file_id: Number(file.id),
    });

    await refreshUnreadCount();
  };

  const handleMoveToArchive = async (file) => {
    const fileId = Number(file?.id || 0);
    if (!clientId || !companyId || !fileId) {
      setArchiveError("Não foi possível identificar o ficheiro selecionado.");
      return;
    }

    try {
      setMovingFileId(fileId);
      setArchiveError("");
      setArchiveSuccess("");

      const res = await moveClientFileToClientArchive({
        company_id: companyId,
        client_id: clientId,
        file_type: "contratos",
        file_id: fileId,
      });

      if (!res?.success) {
        throw new Error(
          res?.message || "Não foi possível mover o ficheiro para o arquivo.",
        );
      }

      await loadContracts();
      await refreshUnreadCount();
      setArchiveSuccess("Ficheiro movido para o arquivo com sucesso.");
    } catch (error) {
      console.error("Move contract to archive error:", error);
      setArchiveError(
        error?.message || "Não foi possível mover o ficheiro para o arquivo.",
      );
    } finally {
      setMovingFileId(0);
    }
  };

  /* ========================= */
  return (
    <Paper sx={{ p: 3, borderRadius: 3 }}>
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
        Contratos
      </Typography>

      {/* TABS */}
      <Tabs
        value={selectedContractTab}
        onChange={(_, v) => setSelectedContractTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          mb: 2,
          backgroundColor: "rgba(114, 4, 241, 0.18)",
          borderRadius: 2,
          "& .MuiTabs-indicator": { display: "none" },
        }}
      >
        {contractCategories.map((cat) => (
          <Tab
            key={cat}
            label={
              (unreadByCategory[cat] || 0) > 0 ? (
                <Badge
                  color="error"
                  badgeContent={unreadByCategory[cat] || 0}
                  anchorOrigin={{ vertical: "top", horizontal: "right" }}
                  sx={{
                    "& .MuiBadge-badge": {
                      minWidth: 14,
                      height: 14,
                      fontSize: "0.65rem",
                      px: 0.5,
                      top: 2,
                      right: -8,
                    },
                  }}
                >
                  <span>{cat}</span>
                </Badge>
              ) : (
                cat
              )
            }
            sx={{
              textTransform: "none",
              "&.Mui-selected": {
                color: "#fff",
                backgroundColor: "#9c27b0",
              },
            }}
          />
        ))}
      </Tabs>

      {/* UPLOAD BUTTON */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Button variant="contained" component="label" color="secondary">
          <UploadFile sx={{ mr: 1 }} />
          Carregar
          <input hidden type="file" onChange={handleContractUpload} />
        </Button>
      </Box>

      {archiveError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {archiveError}
        </Alert>
      ) : null}

      {archiveSuccess ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          {archiveSuccess}
        </Alert>
      ) : null}

      {/* LIST */}
      <List dense>
        {/* ================= DB DOCUMENTS ================= */}
        {filteredDbContracts.map((file) => (
          <ListItem key={file.id} divider sx={{ display: "block" }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Description sx={{ mr: 1, color: "green" }} />

              <ListItemText
                primary={file.nome_original}
                secondary={`${Math.round(file.tamanho / 1024)} KB`}
              />

              <ListItemSecondaryAction>
                <IconButton
                  title="Visualizar documento"
                  color="warning"
                  onClick={() => handleOpenContract(file)}
                  disabled={openingFileId === Number(file.id)}
                >
                  {openingFileId === Number(file.id) ? (
                    <CircularProgress size={18} />
                  ) : (
                    <Visibility />
                  )}
                </IconButton>
                <IconButton
                  color="secondary"
                  title="Mover para Arquivo"
                  onClick={() => handleMoveToArchive(file)}
                  disabled={movingFileId === Number(file.id)}
                >
                  <Archive />
                </IconButton>
                <IconButton
                  title="Eliminar documento"
                  color="error"
                  onClick={() => handleDeleteContract(file)}
                >
                  <Delete />
                </IconButton>
              </ListItemSecondaryAction>
            </Box>

            {/* SHOW DB INFO (READ ONLY) */}
            {![4, 5, 6].includes(selectedContractTab) && (
              <Box sx={{ mt: 1, display: "flex", gap: 1 }}>
                <TextField
                  size="small"
                  value={file.data_inicio || ""}
                  disabled
                  label={
                    selectedContractTab === 2
                      ? "Data Emissao"
                      : selectedContractTab === 3
                        ? "Data"
                        : "Inicio"
                  }
                />

                {selectedContractTab !== 3 && (
                  <TextField
                    size="small"
                    value={file.data_fim || ""}
                    disabled
                    label={
                      selectedContractTab === 2 ? "Prazo Pagamento" : "Fim"
                    }
                  />
                )}
              </Box>
            )}
          </ListItem>
        ))}

        {/* ================= LOCAL DOCUMENTS ================= */}
        {localContract.map((file, index) => (
          <Formik
            key={`${file.name}-${index}`}
            initialValues={{
              inicio: file.inicio || "",
              fim: file.fim || "",
            }}
            validationSchema={getContractSchema(selectedContractTab)}
            enableReinitialize
            onSubmit={async (values) => {
              const payload = {
                ...file,
                ...values,
              };

              const uploadedId = await uploadFileToServer(
                payload,
                contractCategories[selectedContractTab],
              );

              if (uploadedId) {
                setContracts((prev) => ({
                  ...prev,
                  [selectedContractTab]: (
                    prev[selectedContractTab] || []
                  ).filter((_, i) => i !== index),
                }));

                await loadContracts();
                await refreshUnreadCount();
              }
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleSubmit,
              isValid,
              dirty,
            }) => (
              <ListItem divider sx={{ display: "block" }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Description sx={{ mr: 1 }} />

                  <ListItemText
                    primary={file.name}
                    secondary={`${Math.round(file.size / 1024)} KB`}
                  />

                  <ListItemSecondaryAction>
                    <IconButton
                      color="success"
                      onClick={handleSubmit}
                      disabled={
                        !isValid ||
                        (selectedContractTab !== 3 &&
                          values.inicio &&
                          values.fim &&
                          new Date(values.inicio) > new Date(values.fim))
                      }
                    >
                      <Done />
                    </IconButton>

                    <IconButton
                      color="error"
                      onClick={() => removeContract(index)}
                    >
                      <Delete />
                    </IconButton>
                  </ListItemSecondaryAction>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    mt: 1,
                    flexDirection: "column",
                  }}
                >
                  {errors && typeof errors === "string" && (
                    <Typography variant="caption" color="error" sx={{ mb: 1 }}>
                      {errors}
                    </Typography>
                  )}

                  {![4, 5, 6].includes(selectedContractTab) && (
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <TextField
                        label={
                          selectedContractTab === 2
                            ? "Data Emissao"
                            : selectedContractTab === 3
                              ? "Data"
                              : "Data inicio"
                        }
                        name="inicio"
                        type="date"
                        value={values.inicio}
                        onChange={handleChange}
                        error={
                          (touched.inicio && Boolean(errors.inicio)) ||
                          (selectedContractTab !== 3 &&
                            touched.fim &&
                            values.inicio &&
                            values.fim &&
                            new Date(values.inicio) > new Date(values.fim))
                        }
                        helperText={
                          (touched.inicio && errors.inicio) ||
                          (selectedContractTab !== 3 &&
                            touched.fim &&
                            values.inicio &&
                            values.fim &&
                            new Date(values.inicio) > new Date(values.fim) &&
                            (selectedContractTab === 2
                              ? "Data de Emissao nao pode ser maior que Prazo Pagamento"
                              : "Data de inicio nao pode ser maior que data de fim"))
                        }
                        InputLabelProps={{ shrink: true }}
                      />

                      {selectedContractTab !== 3 && (
                        <TextField
                          label={
                            selectedContractTab === 2
                              ? "Prazo Pagamento"
                              : "Data fim"
                          }
                          name="fim"
                          type="date"
                          value={values.fim}
                          onChange={handleChange}
                          error={
                            (touched.fim && Boolean(errors.fim)) ||
                            (touched.fim &&
                              values.inicio &&
                              values.fim &&
                              new Date(values.inicio) > new Date(values.fim))
                          }
                          helperText={
                            (touched.fim && errors.fim) ||
                            (touched.fim &&
                              values.inicio &&
                              values.fim &&
                              new Date(values.inicio) > new Date(values.fim) &&
                              (selectedContractTab === 2
                                ? "Data de Emissao nao pode ser maior que Prazo Pagamento"
                                : "Data de inicio nao pode ser maior que data de fim"))
                          }
                          InputLabelProps={{ shrink: true }}
                        />
                      )}
                    </Box>
                  )}
                </Box>

                {!dirty && file.uploaded && (
                  <Typography variant="caption" color="success.main">
                    Documento enviado com sucesso
                  </Typography>
                )}
              </ListItem>
            )}
          </Formik>
        ))}
      </List>

      <Dialog open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
        <DialogTitle>Eliminar documento</DialogTitle>

        <DialogContent>
          Tens a certeza que queres permanentemente eliminar este documento?
          <br />
          <b>{selectedFile?.nome_original || selectedFile?.name}</b>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDeleteModalOpen(false)}>Cancelar</Button>

          <Button
            color="error"
            variant="contained"
            onClick={confirmDeleteContract}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default Contract;
